import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  CreditCard, Shield, Loader2, Save, CheckCircle2, XCircle,
  Eye, EyeOff, Info, Link2, AlertCircle, Zap, Activity,
} from 'lucide-react';


function SecretInput({ value, onChange, placeholder, testId, masked, onClear }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 pr-20 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {masked && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] text-slate-400 hover:text-red-500 px-2 py-1"
            title="Sostituisci con nuovo valore"
          >
            Modifica
          </button>
        )}
        <button type="button" onClick={() => setShow(!show)} className="text-slate-400 hover:text-[#D44E20] p-1">
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}


export default function AdminPaymentsConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    // Live
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // Test
    stripe_publishable_key_test: '',
    stripe_secret_key_test: '',
    stripe_webhook_secret_test: '',
    // Mode
    stripe_mode: 'live',
    // PayPal LIVE
    paypal_client_id: '',
    paypal_secret: '',
    // PayPal SANDBOX
    paypal_client_id_sandbox: '',
    paypal_secret_sandbox: '',
    paypal_mode: 'live',
    enabled: true,
  });

  const [masked, setMasked] = useState({
    stripe_secret_key: false,
    stripe_webhook_secret: false,
    stripe_secret_key_test: false,
    stripe_webhook_secret_test: false,
    paypal_secret: false,
    paypal_secret_sandbox: false,
  });

  const load = () => {
    setLoading(true);
    adminApi.get('/admin/integrations/payments')
      .then(r => {
        const d = r.data;
        setConfig(d);
        setForm({
          stripe_publishable_key: d.stripe_publishable_key || '',
          stripe_secret_key: d.stripe_secret_key_masked || '',
          stripe_webhook_secret: d.stripe_webhook_secret_masked || '',
          stripe_publishable_key_test: d.stripe_publishable_key_test || '',
          stripe_secret_key_test: d.stripe_secret_key_test_masked || '',
          stripe_webhook_secret_test: d.stripe_webhook_secret_test_masked || '',
          stripe_mode: d.stripe_mode || 'live',
          paypal_client_id: d.paypal_client_id || '',
          paypal_secret: d.paypal_secret_masked || '',
          paypal_client_id_sandbox: d.paypal_client_id_sandbox || '',
          paypal_secret_sandbox: d.paypal_secret_sandbox_masked || '',
          paypal_mode: d.paypal_mode || 'live',
          enabled: !!d.enabled,
        });
        setMasked({
          stripe_secret_key: !!d.stripe_secret_key_masked,
          stripe_webhook_secret: !!d.stripe_webhook_secret_masked,
          stripe_secret_key_test: !!d.stripe_secret_key_test_masked,
          stripe_webhook_secret_test: !!d.stripe_webhook_secret_test_masked,
          paypal_secret: !!d.paypal_secret_masked,
          paypal_secret_sandbox: !!d.paypal_secret_sandbox_masked,
        });
      })
      .catch(() => toast.error('Errore caricamento configurazione'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        stripe_publishable_key: form.stripe_publishable_key.trim(),
        stripe_secret_key: form.stripe_secret_key,
        stripe_webhook_secret: form.stripe_webhook_secret,
        stripe_publishable_key_test: form.stripe_publishable_key_test.trim(),
        stripe_secret_key_test: form.stripe_secret_key_test,
        stripe_webhook_secret_test: form.stripe_webhook_secret_test,
        stripe_mode: form.stripe_mode,
        paypal_client_id: form.paypal_client_id.trim(),
        paypal_secret: form.paypal_secret,
        paypal_client_id_sandbox: form.paypal_client_id_sandbox.trim(),
        paypal_secret_sandbox: form.paypal_secret_sandbox,
        paypal_mode: form.paypal_mode,
        enabled: form.enabled,
        keep_stripe_secret: masked.stripe_secret_key && form.stripe_secret_key.startsWith('•'),
        keep_stripe_webhook: masked.stripe_webhook_secret && form.stripe_webhook_secret.startsWith('•'),
        keep_stripe_secret_test: masked.stripe_secret_key_test && form.stripe_secret_key_test.startsWith('•'),
        keep_stripe_webhook_test: masked.stripe_webhook_secret_test && form.stripe_webhook_secret_test.startsWith('•'),
        keep_paypal_secret: masked.paypal_secret && form.paypal_secret.startsWith('•'),
        keep_paypal_secret_sandbox: masked.paypal_secret_sandbox && form.paypal_secret_sandbox.startsWith('•'),
      };
      const r = await adminApi.put('/admin/integrations/payments', payload);
      toast.success('Configurazione salvata');
      setConfig(prev => ({ ...prev, has_stripe: r.data.has_stripe, has_stripe_test: r.data.has_stripe_test, has_paypal: r.data.has_paypal, has_paypal_sandbox: r.data.has_paypal_sandbox, enabled: r.data.enabled, stripe_mode: r.data.stripe_mode, paypal_mode: r.data.paypal_mode }));
      // Notify AdminLayout so the persistent test-mode banner refreshes immediately.
      window.dispatchEvent(new CustomEvent('payments-config-updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
    setSaving(false);
  };

  const clearSecret = (key) => {
    setForm(f => ({ ...f, [key]: '' }));
    setMasked(m => ({ ...m, [key]: false }));
  };

  const testConnections = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await adminApi.post('/admin/integrations/payments/test');
      setTestResult(r.data);
      if (r.data.stripe?.ok && r.data.paypal?.ok) {
        toast.success('Tutte le connessioni funzionano!');
      } else if (r.data.stripe?.ok || r.data.paypal?.ok) {
        toast.info('Verifica completata — alcuni servizi necessitano attenzione');
      } else {
        toast.error('Nessuna connessione riuscita — controlla le chiavi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore test connessioni');
    }
    setTesting(false);
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" size={28} /></div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="payments-config-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1">Pagamenti online</h1>
          <p className="text-sm text-slate-500">Collega Stripe e PayPal per incassare direttamente sul tuo account i pagamenti di <b>biglietti eventi</b>, <b>shop/merchandising</b> e <b>ordini delivery</b>.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-enabled-label">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={e => setForm({ ...form, enabled: e.target.checked })}
            data-testid="toggle-enabled"
            className="w-4 h-4 accent-[#D44E20]"
          />
          <span className="text-sm font-semibold text-[#1A1A1A]">Pagamenti attivi</span>
        </label>
      </div>

      {/* Future Stripe Connect banner */}
      <div className="bg-gradient-to-r from-[#FDECE7] to-[#FFF5F0] border border-[#F8B9A1] rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="connect-banner">
        <Zap size={18} className="text-[#D44E20] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">A breve: onboarding 1-click con Stripe Connect</p>
          <p className="text-xs text-slate-600 mt-1">
            Stiamo integrando <b>Stripe Connect</b> e <b>PayPal Commerce Platform</b> — invece di incollare le chiavi ti basterà autorizzare Lazo con un click. Per ora questa configurazione manuale è la via più veloce per partire.
          </p>
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatusCard
          ok={config?.has_stripe}
          title="Stripe"
          subtitle={config?.has_stripe ? 'Configurato' : 'Non configurato'}
          testId="status-stripe"
          testResult={testResult?.stripe}
        />
        <StatusCard
          ok={config?.has_paypal}
          title="PayPal"
          subtitle={config?.has_paypal ? `Configurato (${config?.paypal_mode})` : 'Non configurato'}
          testId="status-paypal"
          testResult={testResult?.paypal}
        />
      </div>

      {/* Test connection button */}
      <div className="mb-6">
        <button
          onClick={testConnections}
          disabled={testing}
          data-testid="test-connections-btn"
          className="w-full px-4 py-2.5 bg-white border-2 border-dashed border-slate-300 hover:border-[#D44E20] hover:bg-[#FDECE7]/30 rounded-xl text-sm font-semibold text-slate-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
          {testing ? 'Verifica in corso…' : 'Testa connessioni (nessun addebito)'}
        </button>
      </div>

      {/* Stripe section */}
      <Section icon={CreditCard} title="Stripe" testId="stripe-section">
        {/* Mode toggle */}
        <Field label="Modalità Stripe attiva">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, stripe_mode: 'live' })}
              data-testid="stripe-mode-live"
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                form.stripe_mode === 'live'
                  ? 'bg-[#10B981] border-[#10B981] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'
              }`}
            >
              🟢 Live (pagamenti reali)
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, stripe_mode: 'test' })}
              data-testid="stripe-mode-test"
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                form.stripe_mode === 'test'
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'
              }`}
            >
              🧪 Test (sandbox, no addebiti reali)
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Switch al volo tra Live e Test — entrambe le chiavi restano salvate.
          </p>
        </Field>

        {/* Big warning banner when Test mode is ACTIVE */}
        {form.stripe_mode === 'test' && (
          <div className="rounded-xl bg-amber-50 border-2 border-amber-400 p-3" data-testid="test-mode-banner">
            <p className="text-sm font-black text-amber-900 flex items-center gap-2">
              <AlertCircle size={16} />⚠️ MODALITÀ TEST ATTIVA
            </p>
            <p className="text-[11px] text-amber-800 mt-1 leading-relaxed">
              I pagamenti che riceverai sono <b>finti</b>. Nessun cliente reale potrà acquistare biglietti / ordini fino a quando non rimetti la modalità Live.
              <br />Usa carte test (es. <code className="bg-white px-1 rounded font-mono">4242 4242 4242 4242</code>, qualsiasi scadenza futura, CVC <code className="bg-white px-1 rounded font-mono">123</code>).
            </p>
          </div>
        )}

        <StepGuide
          title={form.stripe_mode === 'test' ? 'Come ottenere le chiavi TEST Stripe' : 'Come ottenere le chiavi LIVE Stripe'}
          steps={form.stripe_mode === 'test' ? [
            <>Vai su <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] font-semibold underline">dashboard.stripe.com/test/apikeys</a> (toggle "Test mode" attivo).</>,
            <>Copia <b>Publishable key</b> (<code className="text-[11px]">pk_test_…</code>) e <b>Secret key</b> (<code className="text-[11px]">sk_test_…</code>).</>,
            <>Crea un webhook su <a href="https://dashboard.stripe.com/test/webhooks" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] font-semibold underline">test/webhooks</a> con lo stesso URL del Live e copia il <code className="text-[11px]">whsec_test_…</code>.</>,
            <>Compila i campi TEST qui sotto e salva.</>,
          ] : [
            <>Vai su <a href="https://dashboard.stripe.com/login" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] font-semibold underline">dashboard.stripe.com</a> e accedi al tuo account.</>,
            <>Attiva la <b>modalità Live</b> (disattiva "Test mode" in alto a sinistra).</>,
            <>Menu → <b>Developers</b> → <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#635BFF] font-semibold underline">API keys</a>.</>,
            <>Copia <b>Publishable key</b> (<code className="text-[11px]">pk_live_…</code>) e <b>Secret key</b> (<code className="text-[11px]">sk_live_…</code>).</>,
          ]}
        />

        {/* LIVE keys */}
        <div className={`rounded-xl border-2 p-4 space-y-3 ${form.stripe_mode === 'live' ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}>
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
            🟢 Chiavi LIVE {form.stripe_mode === 'live' && <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">ATTIVE</span>}
          </p>
          <Field label="Publishable key (pk_live_...)">
            <input
              value={form.stripe_publishable_key}
              onChange={e => setForm({ ...form, stripe_publishable_key: e.target.value })}
              placeholder="pk_live_..."
              data-testid="stripe-publishable"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]"
            />
          </Field>
          <Field label="Secret key (sk_live_...)">
            <SecretInput
              value={form.stripe_secret_key}
              onChange={v => { setForm({ ...form, stripe_secret_key: v }); if (masked.stripe_secret_key && !v.startsWith('•')) setMasked(m => ({ ...m, stripe_secret_key: false })); }}
              placeholder="sk_live_..."
              testId="stripe-secret"
              masked={masked.stripe_secret_key}
              onClear={() => clearSecret('stripe_secret_key')}
            />
          </Field>
          <Field label="Webhook signing secret LIVE (whsec_...)">
            <SecretInput
              value={form.stripe_webhook_secret}
              onChange={v => { setForm({ ...form, stripe_webhook_secret: v }); if (masked.stripe_webhook_secret && !v.startsWith('•')) setMasked(m => ({ ...m, stripe_webhook_secret: false })); }}
              placeholder="whsec_..."
              testId="stripe-webhook"
              masked={masked.stripe_webhook_secret}
              onClear={() => clearSecret('stripe_webhook_secret')}
            />
          </Field>
        </div>

        {/* TEST keys */}
        <div className={`rounded-xl border-2 p-4 space-y-3 ${form.stripe_mode === 'test' ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-white'}`}>
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
            🧪 Chiavi TEST (sandbox) {form.stripe_mode === 'test' && <span className="text-[9px] bg-amber-600 text-white px-1.5 py-0.5 rounded">ATTIVE</span>}
          </p>
          <Field label="Publishable key (pk_test_...)">
            <input
              value={form.stripe_publishable_key_test}
              onChange={e => setForm({ ...form, stripe_publishable_key_test: e.target.value })}
              placeholder="pk_test_..."
              data-testid="stripe-publishable-test"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </Field>
          <Field label="Secret key (sk_test_...)">
            <SecretInput
              value={form.stripe_secret_key_test}
              onChange={v => { setForm({ ...form, stripe_secret_key_test: v }); if (masked.stripe_secret_key_test && !v.startsWith('•')) setMasked(m => ({ ...m, stripe_secret_key_test: false })); }}
              placeholder="sk_test_..."
              testId="stripe-secret-test"
              masked={masked.stripe_secret_key_test}
              onClear={() => clearSecret('stripe_secret_key_test')}
            />
          </Field>
          <Field label="Webhook signing secret TEST (whsec_test_...)">
            <SecretInput
              value={form.stripe_webhook_secret_test}
              onChange={v => { setForm({ ...form, stripe_webhook_secret_test: v }); if (masked.stripe_webhook_secret_test && !v.startsWith('•')) setMasked(m => ({ ...m, stripe_webhook_secret_test: false })); }}
              placeholder="whsec_..."
              testId="stripe-webhook-test"
              masked={masked.stripe_webhook_secret_test}
              onClear={() => clearSecret('stripe_webhook_secret_test')}
            />
          </Field>
        </div>

        {/* Webhook URL info */}
        <div className="rounded-xl bg-amber-50/60 border border-amber-200 p-3 mt-2">
          <p className="text-xs font-bold text-amber-900 flex items-center gap-1.5 mb-2">
            <AlertCircle size={13} /> URL del webhook (uguale per Live e Test)
          </p>
          <ol className="text-[11px] text-amber-900 space-y-1.5 list-decimal ml-4 leading-relaxed">
            <li>
              Su Stripe Dashboard, in <b>Live mode</b> usa <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline font-semibold">/webhooks</a>; in <b>Test mode</b> usa <a href="https://dashboard.stripe.com/test/webhooks" target="_blank" rel="noopener noreferrer" className="underline font-semibold">/test/webhooks</a>.
            </li>
            <li>
              Come URL endpoint incolla:
              <div className="bg-white border border-amber-200 rounded-md p-2 mt-1 font-mono text-[11px]" data-testid="webhook-urls">
                <div className="flex items-center justify-between gap-2">
                  <span className="break-all">{window.location.origin}/api/webhook/stripe</span>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/webhook/stripe`); toast.success('Copiato'); }}
                    className="text-[9px] bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold px-2 py-0.5 rounded">COPIA</button>
                </div>
              </div>
            </li>
            <li>
              Seleziona i 5 eventi:
              <code className="text-[10px] block mt-1">checkout.session.completed · checkout.session.async_payment_succeeded · checkout.session.expired · charge.refunded · charge.dispute.created</code>
            </li>
            <li>
              Dopo aver creato 2 webhook (uno in Live, uno in Test) → copia i 2 signing secret nei rispettivi campi qui sopra.
            </li>
          </ol>
        </div>
      </Section>

      {/* PayPal section */}
      <Section icon={Shield} title="PayPal" testId="paypal-section">
        <StepGuide
          title={form.paypal_mode === 'sandbox' ? 'Come ottenere le chiavi PayPal SANDBOX' : 'Come ottenere le chiavi PayPal LIVE'}
          steps={form.paypal_mode === 'sandbox' ? [
            <>Vai su <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="text-[#0070BA] font-semibold underline">developer.paypal.com → Apps & Credentials → Sandbox</a>.</>,
            <>Crea (o apri) una <b>Sandbox App</b> di tipo Merchant.</>,
            <>Copia il <b>Client ID</b> e il <b>Secret</b> generati per la sandbox.</>,
            <>Test con account buyer sandbox creabili da <a href="https://developer.paypal.com/dashboard/accounts" target="_blank" rel="noopener noreferrer" className="text-[#0070BA] font-semibold underline">Sandbox accounts</a>.</>,
          ] : [
            <>Vai su <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noopener noreferrer" className="text-[#0070BA] font-semibold underline">developer.paypal.com</a> e accedi con il tuo account <b>PayPal Business</b>.</>,
            <>In alto: seleziona il tab <b>Live</b> (NO Sandbox per produzione).</>,
            <>Menu → <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" className="text-[#0070BA] font-semibold underline">Apps & Credentials</a> → se non hai un'app, <b>Create App</b> (tipo: Merchant).</>,
            <>Apri l'app e copia <b>Client ID</b> e <b>Secret</b> (click "Show" per il secret).</>,
          ]}
        />

        <Field label="Modalità PayPal attiva">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, paypal_mode: 'live' })}
              data-testid="paypal-mode-live"
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                form.paypal_mode === 'live'
                  ? 'bg-[#10B981] border-[#10B981] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'
              }`}
            >
              🟢 Live (pagamenti reali)
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, paypal_mode: 'sandbox' })}
              data-testid="paypal-mode-sandbox"
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                form.paypal_mode === 'sandbox'
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'
              }`}
            >
              🧪 Sandbox (test, no addebiti reali)
            </button>
          </div>
        </Field>

        {/* Big warning banner when Sandbox mode is ACTIVE */}
        {form.paypal_mode === 'sandbox' && (
          <div className="rounded-xl bg-amber-50 border-2 border-amber-400 p-3" data-testid="paypal-sandbox-banner">
            <p className="text-sm font-black text-amber-900 flex items-center gap-2">
              <AlertCircle size={16} />⚠️ MODALITÀ SANDBOX ATTIVA
            </p>
            <p className="text-[11px] text-amber-800 mt-1 leading-relaxed">
              I pagamenti PayPal sono <b>finti</b>. Usa account buyer di test creati su <a href="https://developer.paypal.com/dashboard/accounts" target="_blank" rel="noopener noreferrer" className="underline font-semibold">developer.paypal.com → Sandbox accounts</a>. Ricordati di tornare a Live prima del go-live.
            </p>
          </div>
        )}

        <Field label="Client ID">
          <input
            value={form.paypal_mode === 'sandbox' ? form.paypal_client_id_sandbox : form.paypal_client_id}
            onChange={e => setForm({ ...form, [form.paypal_mode === 'sandbox' ? 'paypal_client_id_sandbox' : 'paypal_client_id']: e.target.value })}
            placeholder={form.paypal_mode === 'sandbox' ? 'AeB...(sandbox)' : 'AeB...(live)'}
            data-testid={form.paypal_mode === 'sandbox' ? 'paypal-client-id-sandbox' : 'paypal-client-id'}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20]"
          />
        </Field>

        <Field label="Secret">
          {form.paypal_mode === 'sandbox' ? (
            <SecretInput
              value={form.paypal_secret_sandbox}
              onChange={v => { setForm({ ...form, paypal_secret_sandbox: v }); if (masked.paypal_secret_sandbox && !v.startsWith('•')) setMasked(m => ({ ...m, paypal_secret_sandbox: false })); }}
              placeholder="ELm...(sandbox)"
              testId="paypal-secret-sandbox"
              masked={masked.paypal_secret_sandbox}
              onClear={() => clearSecret('paypal_secret_sandbox')}
            />
          ) : (
            <SecretInput
              value={form.paypal_secret}
              onChange={v => { setForm({ ...form, paypal_secret: v }); if (masked.paypal_secret && !v.startsWith('•')) setMasked(m => ({ ...m, paypal_secret: false })); }}
              placeholder="ELm...(live)"
              testId="paypal-secret"
              masked={masked.paypal_secret}
              onClear={() => clearSecret('paypal_secret')}
            />
          )}
        </Field>

        <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3 text-[11px] text-blue-900 leading-relaxed">
          <Info size={13} className="inline mr-1" />
          Stai modificando le chiavi <b>{form.paypal_mode === 'sandbox' ? 'SANDBOX' : 'LIVE'}</b>. Cambia modalità sopra per modificare le altre — le due coppie di chiavi sono memorizzate separatamente.
          <span className="block mt-1 text-[10px] text-blue-700">
            Live salvato: {config?.has_paypal ? '✓' : '—'} · Sandbox salvato: {config?.has_paypal_sandbox ? '✓' : '—'}
          </span>
        </div>
      </Section>

      {/* Save button */}
      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-payments-btn"
          className="px-6 py-3 bg-[#D44E20] text-white rounded-xl font-semibold shadow-lg hover:bg-[#B8421B] disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Salvataggio…' : 'Salva configurazione'}
        </button>
      </div>
    </div>
  );
}


function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#D44E20]" />
        <h3 className="text-base font-bold text-[#1A1A1A]">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}


function Field({ label, children, help }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
      {help && (
        <p className="text-[11px] text-slate-500 mt-1.5 flex items-start gap-1">
          <AlertCircle size={10} className="mt-0.5 flex-shrink-0" />
          <span className="break-all">{help}</span>
        </p>
      )}
    </div>
  );
}


function Info2({ text, link }) {
  return (
    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-100">
      <Info size={13} className="text-blue-600 flex-shrink-0 mt-0.5" />
      <div className="text-[11px] text-blue-900 leading-relaxed">
        {text}
        {link && (
          <>
            {' '}
            <a href={link} target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-700 inline-flex items-center gap-0.5">
              Apri <Link2 size={9} />
            </a>
          </>
        )}
      </div>
    </div>
  );
}


function StepGuide({ title, steps }) {
  return (
    <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3" data-testid="step-guide">
      <p className="text-xs font-bold text-blue-900 flex items-center gap-1.5 mb-2">
        <Info size={13} /> {title}
      </p>
      <ol className="text-[11px] text-blue-900 space-y-1.5 list-decimal ml-4 leading-relaxed">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </div>
  );
}


function StatusCard({ ok, title, subtitle, testId, testResult }) {
  const testOk = testResult?.ok === true;
  const testFail = testResult && testResult.ok === false;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-3" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${ok ? 'bg-emerald-50' : 'bg-slate-100'}`}>
        {ok ? <CheckCircle2 size={18} className="text-emerald-600" /> : <XCircle size={18} className="text-slate-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-[#1A1A1A]">{title}</p>
        <p className={`text-xs ${ok ? 'text-emerald-700' : 'text-slate-500'}`}>{subtitle}</p>
        {testResult && (
          <p className={`text-[10px] mt-1 font-semibold truncate ${testOk ? 'text-emerald-600' : testFail ? 'text-red-600' : 'text-slate-500'}`}>
            {testOk ? '✓ ' : testFail ? '✗ ' : ''}{testResult.message}
          </p>
        )}
      </div>
    </div>
  );
}
